import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { plural, t } from '@lingui/core/macro';

import SimplifiedPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/SimplifiedPriceColumn';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { SearchParamsPetType } from '@rover/constants/src/translations/sitterProfilePage.constants';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiBrightHorizonsSitterProfileBannerRetrieve } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-sitter-profile-banner-retrieve';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import { getParseCancellationDays } from '../../../../../utils/getParseCancellationDays';
import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import {
  PtCondensedBanner,
  PtCondensedPetTypeDescription,
} from '../../../../search/SearchPage/components/PtCondensedBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';
import { SitterPriceBanner } from '../SitterPriceBanner';

import RevampAdditionalRates from './RevampAdditionalRates';
import { CTAPropsCommon } from './RevampServicesCta';
import { serviceIconToGraphic } from './utils';

type Props = {
  handleOpenSheet: () => void;
  isOpen: boolean;
  cancellationPolicy?: string | undefined;
};

const RevampServicesCtaCommon = ({
  priceTransparencyLevel,
  language,
  petType,
  petCount,
  bookingFeePercentage,
  handleOpenSheet,
  isOpen,
  cancellationPolicy,
}: CTAPropsCommon & Props): JSX.Element => {
  const theme = useTheme();
  const { sitterData, serviceType, selectedService, isBrightHorizonsUser } = useRevampCta();
  const {
    countryConfigUrls,
    isPetCareModes,
    isPtFull,
    isPtCondensed,
    displayPetType,
    petTypeServices,
    rate,
    price,
  } = useRevampCtaCommon(priceTransparencyLevel, petType, petCount, selectedService);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  const { showSittersRates } = useRevampProfileContext();
  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { slug = '' } = useParams<{ slug: string }>();
  const { data: brightHorizonsBanner } = useApiBrightHorizonsSitterProfileBannerRetrieve(
    {
      serviceType: serviceType || 'boarding',
      slug,
      petType: petType || SearchParamsPetType.DOG_CAT,
      dogCount: petCount.selectedDogCount,
      catCount: petCount.selectedCatCount,
      puppyCount: petCount.selectedPuppyCount,
    },
    { query: { enabled: isBrightHorizonsUser } }
  );

  const { formattedPrice, beforePriceText, afterPriceText } = usePriceDisplayInfo({
    currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
    priceTransparencyLevel,
    language,
    price: price ?? '',
    priceInCredits,
    shouldShowPriceInCredits,
    showFromText: !shouldShowPriceInCredits,
    unitAsSentence: rate?.unitAsSentence ?? '',
    isBHUser: isBrightHorizonsUser,
  });

  const showingBH = !!brightHorizonsBanner?.bannerMessage;

  type iconVariant = 'away' | 'selected' | 'dropItem';

  const renderServiceIcon = (iconKey: string, variant: iconVariant): JSX.Element | null => {
    const IconComponent = serviceIconToGraphic[iconKey];
    if (!IconComponent) return null;
    let fillColor: string;
    switch (variant) {
      case 'away':
        fillColor = theme.colors.text.disabled;
        break;
      case 'selected':
        fillColor = theme.colors.interactive_text.primary_base;
        break;
      case 'dropItem':
        fillColor = theme.colors.text.success;
        break;
      default:
        fillColor = theme.colors.interactive_text.primary_base;
    }
    return (
      <Box width="24px" height="24px" flexShrink="0">
        <IconComponent width="24" height="24" aria-hidden="true" fill={fillColor} />
      </Box>
    );
  };

  type RenderDropMenuItemVariant = 'selected' | 'dropItem';

  const renderDropMenuItem = (
    service: EnhancedPersonSitterProfileService,
    variant: RenderDropMenuItemVariant = 'dropItem'
  ): JSX.Element => {
    return (
      <>
        <Flex flexDirection="row" gap="2x">
          <Text
            size="300"
            fontWeight="semibold"
            textColor={service.isAway ? 'disabled' : 'primary'}
          >
            {service.displayName}
          </Text>
          {service.isAway && variant === 'dropItem' && <Badge text={t`Away`} variant="tertiary" />}
        </Flex>
        {service.description && (
          <Text size="200" textColor={service.isAway ? 'disabled' : 'secondary'}>
            {service.description}
          </Text>
        )}
      </>
    );
  };

  const getCancellationPolicyText = (): string | undefined => {
    const days = cancellationPolicy ? getParseCancellationDays(cancellationPolicy) : undefined;
    if (days === undefined) return undefined;
    if (days === 0) return t`Cancel <0>before</0> the booking begins.`.replace(/<\/?0>/g, '');
    const daysText = plural(days, { one: '# day', other: '# days' });
    return t`Cancel up to <0>${daysText}</0> before.`.replace(/<\/?0>/g, '');
  };

  const priceText =
    rate && price
      ? [beforePriceText, formattedPrice, afterPriceText].filter(Boolean).join(' ')
      : undefined;

  const serviceText = selectedService
    ? [selectedService.displayName, selectedService.description].filter(Boolean).join('. ')
    : t`Select a service`;

  const accessibilityMessage =
    [serviceText, priceText, getCancellationPolicyText()].filter(Boolean).join('. ') || undefined;

  return (
    <>
      <Flex
        data-testid="revamp-service-selector-trigger"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        onPress={handleOpenSheet}
        webAs="button"
        aria-label={accessibilityMessage}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        border="none"
        p="2x"
        sx={{
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {(selectedService && (
          <Flex gap="3x">
            {renderServiceIcon(selectedService.icon, selectedService.isAway ? 'away' : 'selected')}
            <Flex flexDirection="column" alignItems="flex-start">
              {renderDropMenuItem(selectedService, 'selected')}
            </Flex>
          </Flex>
        )) || <Text size="300">{t`Select a service`}</Text>}

        <Box
          width="24px"
          height="24px"
          flexShrink="0"
          sx={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
          aria-hidden="true"
        >
          <DropdownSmall
            width="24"
            height="24"
            fill={selectedService?.isAway ? theme.colors.text.disabled : theme.colors.text.primary}
          />
        </Box>
      </Flex>

      <Flex flexDirection="column" gap="2x" sx={{ paddingLeft: '44px' }}>
        {rate && price && (
          <SimplifiedPriceColumn
            currencyCode={rate.pricePerUnitObj.currencyIso}
            priceTransparencyLevel={priceTransparencyLevel}
            language={language}
            price={price}
            priceInCredits={priceInCredits}
            shouldShowPriceInCredits={shouldShowPriceInCredits}
            showFromText={!shouldShowPriceInCredits}
            unitAsSentence={rate?.unitAsSentence}
            isBHUser={isBrightHorizonsUser}
            variant="cta-desktop"
          />
        )}

        {isPtCondensed && <PtCondensedPetTypeDescription petType={displayPetType} />}

        <RevampAdditionalRates
          services={sitterData.services}
          serviceType={serviceType}
          petType={petType}
          petCount={petCount}
          isSitterInPetCareModesExperiment={isPetCareModes}
        />
      </Flex>
      {showingBH && (
        <Box mb="-4x">
          <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
        </Box>
      )}
      {isPtCondensed ? (
        <PtCondensedBanner
          priceTransparencyLevel={priceTransparencyLevel}
          bookingFeePercentage={bookingFeePercentage}
          onClick={() => setIsFeeExplanationModalOpen(true)}
          isSitterProfile
        />
      ) : (
        !showingBH &&
        isPtFull && (
          <SitterPriceBanner
            petCounts={petCount}
            petType={petType}
            serviceType={serviceType}
            countryConfigUrls={countryConfigUrls}
            bookingFeePercentage={bookingFeePercentage}
            services={petTypeServices}
            version="revamp"
          />
        )
      )}
      <FeeExplanationModal
        isOpen={isFeeExplanationModalOpen}
        onClose={() => setIsFeeExplanationModalOpen(false)}
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.lite_condensed}
        bookingPercentageFee={bookingFeePercentage}
      />
    </>
  );
};

export default RevampServicesCtaCommon;
